import { Fragment, useState, type HTMLAttributes } from "react";
import { cn } from "../utils/cn";
import { Mono, Text } from "../primitives/typography";
import { gradient } from "../tokens/gradients";
import { BadgeCheck, ChevronDown, ChevronUp, Link, ListVideo, MoreVertical, Pencil, Play, Trash } from "lucide-react";
import { formatDuration, isOverdue } from "../../../../apps/global/formatHelpers";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./dropdown-menu";
import { Badge, badgeVariants } from "./badge";
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from "./tooltip";

export interface ContentTileProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
    title?: string | null;
    posterUrl?: string | null;
    description?: string | null;
    dueDate?: string | null;
    /**
     * Renders the stacked-thumbnail effect and swaps the corner badge to a `playlistLabel`
     * pill instead of a duration. A playlist has no one length to show.
     */
    isPlaylist?: boolean;
    /** Corner pill label for a playlist, e.g. "Mix" or "12 videos". Ignored unless `isPlaylist`. */
    playlistLabel?: string;
    /** Renders a duration chip via `formatDuration`. Ignored when `isPlaylist`. */
    durationSeconds?: number | null;
    /** Shown alongside `publishedStatus` / `processingStatus` in the status line. */
    organizationName?: string | null;
    meta?: string | null;
    status?: string | null;
    /**
     * How `status` is coloured. The caller maps its own states to a tone — the tile knows only
     * the copy and which token each tone means, never what "Completed" is.
     */
    statusTone?: StatusTone;
    /**
     * How far through, e.g. "2 of 4 steps". Sits between the status and the due date.
     *
     * The caller decides when it is worth saying — a course nobody has opened has no progress to
     * report, and a finished one has nothing left to say beyond "Completed".
     */
    progress?: string | null;
    completed?: boolean;
    /**
     * Renders a "more options" (⋮) menu next to the title, with "Edit", "Copy link" and "Delete"
     * items — the same actions `MediaCard`'s footer exposes as standalone buttons. Provide at
     * least one to show the menu; provide none and it is not rendered at all.
     */
    onEditClicked?: () => void;
    onCopyLinkClicked?: () => void;
    onDeleteClicked?: () => void;
    /**
     * At most 2 tags shown, with a "+N more" chip for the rest. Takes the tags as they come back
     * from the API — only `name` is read, so both the `{ id, name }` and `{ name }` selections fit.
     */
    tags?: { name: string }[];
    /** Label for the "+N" overflow chip when there are more than 2 tags. */
    moreTagsLabel?: string;
    /** `"PUBLISHED"` shows "Published" with a checkmark next to `organizationName`; anything else shows "Draft". */
    publishedStatus?: string;
    /** `"READY"` shows "Ready" with a checkmark next to `organizationName`; anything else shows "Draft". */
    processingStatus?: string;
    /**
     * Preview this item. Omit and the play mark is not rendered at all.
     *
     * It used to be drawn unconditionally as a plain `<div>` with no handler — decorative chrome
     * that looked exactly like a control, so on the Library page clicking it did nothing and there
     * was nothing to discover. A play mark now means something is wired to it.
     */
    onPlayClicked?: () => void;
}

export type StatusTone = "default" | "secondary" | "success" | "warning";

const STATUS_TONE_CLASS: Record<StatusTone, string> = {
    default: "text-text-muted",
    secondary: "text-primary",
    success: "text-text-success",
    warning: "text-warning",
};

function TileDescription({ description }: { description: string }) {
    const [
        expanded,
        setExpanded,
    ] = useState(false);

    return (
        <button
            type="button"
            aria-expanded={expanded}
            onClick={(e) => {
                e.stopPropagation();
                setExpanded((prev) => !prev);
            }}
            className="flex w-full items-start gap-1.5 text-left"
        >
            <Text size="xs" muted className={cn("leading-snug wrap-break-word", !expanded && "line-clamp-2")}>
                {description}
            </Text>
            {expanded ? (
                <ChevronUp className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0" />
            ) : (
                <ChevronDown className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0" />
            )}
        </button>
    );
}

const ContentTile = ({
    className,
    title,
    posterUrl,
    description,
    dueDate,
    status,
    statusTone = "default",
    progress,
    completed,
    isPlaylist,
    playlistLabel = "Mix",
    durationSeconds,
    organizationName,
    meta,
    publishedStatus,
    processingStatus,
    tags,
    moreTagsLabel = "more",
    onEditClicked,
    onCopyLinkClicked,
    onDeleteClicked,
    onClick,
    onPlayClicked,
    ...props
}: ContentTileProps) => {
    const clickable = Boolean(onClick);

    /*
     * Status and due date share a row, and it survives either being absent: every assignment has
     * a status, only some have a deadline.
     */
    const overdue = isOverdue(dueDate, completed);
    const dueLabel = dueDate
        ? overdue
            ? `${new Date(dueDate).toLocaleDateString()} · overdue`
            : `Due ${new Date(dueDate).toLocaleDateString()}`
        : null;

    return (
        <div
            className={cn(
                "group/tile relative isolate mb-2 flex w-full flex-col",
                clickable && "cursor-pointer",
                className,
            )}
            onClick={onClick}
            {...props}
        >
            <div
                aria-hidden="true"
                className="bg-accent pointer-events-none absolute -inset-2 -z-10 rounded-xl opacity-0 transition-opacity duration-200 ease-out group-hover/tile:opacity-100"
            />
            <div className="relative">
                {isPlaylist && (
                    <>
                        <div className="bg-border absolute inset-x-3 -top-2 h-3 rounded-t-lg" />
                        <div className="bg-muted absolute inset-x-1.5 -top-1 h-3.5 rounded-t-lg" />
                    </>
                )}
                <div
                    className={cn(
                        "relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-xl bg-linear-to-br",
                        gradient,
                    )}
                >
                    {posterUrl ? (
                        <img
                            src={posterUrl}
                            alt={`${title}-video-poster`}
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 ease-out group-hover/tile:scale-105"
                        />
                    ) : (
                        <svg
                            className="absolute inset-0 h-full w-full opacity-[0.07] transition-transform duration-300 ease-out group-hover/tile:scale-105"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            {[
                                60,
                                100,
                                140,
                                180,
                                220,
                                260,
                                300,
                            ].map((r) => (
                                <circle key={r} cx="100%" cy="100%" r={r} fill="none" stroke="white" strokeWidth="1" />
                            ))}
                        </svg>
                    )}
                    {isPlaylist ? (
                        <span className="absolute right-2 bottom-2 flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
                            <ListVideo className="h-3 w-3" />
                            {playlistLabel}
                        </span>
                    ) : (
                        durationSeconds !== undefined && (
                            <span className="absolute right-2 bottom-2 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[10px] font-medium text-white">
                                {formatDuration(durationSeconds)}
                            </span>
                        )
                    )}
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(0,0,0,0.3)_100%)]" />
                    {onPlayClicked && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <button
                                type="button"
                                aria-label={`Play ${title ?? "video"}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onPlayClicked();
                                }}
                                className="group focus-visible:ring-ring absolute inset-0 flex items-center justify-center focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
                            >
                                <span className="flex h-12 w-12 items-center justify-center rounded-full border-[0.5px] border-white/60 bg-white/20 backdrop-blur-sm transition-colors group-hover:bg-white/40">
                                    <Play className="h-3 w-3 fill-white text-white" />
                                </span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-3 flex items-start gap-2">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-col items-start justify-between">
                        <div className="flex w-full items-start justify-between gap-2">
                            <Text
                                weight="semibold"
                                className="mb-1 line-clamp-2 min-w-0 flex-1 leading-snug wrap-break-word"
                            >
                                {title}
                            </Text>

                            {(onEditClicked || onCopyLinkClicked || onDeleteClicked) && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button
                                            type="button"
                                            aria-label="More options"
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10 -mr-1 shrink-0 rounded-xl p-1.5"
                                        >
                                            <MoreVertical className="h-4 w-4" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                        className="bg-sidebar border-sidebar-border w-20"
                                        align="end"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {onEditClicked && (
                                            <DropdownMenuItem
                                                onSelect={() => {
                                                    setTimeout(() => onEditClicked(), 0);
                                                }}
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                                Edit
                                            </DropdownMenuItem>
                                        )}
                                        {onCopyLinkClicked && (
                                            <DropdownMenuItem
                                                onSelect={() => {
                                                    // Deferred: Radix's own close cleanup for this menu (which
                                                    // resets `pointer-events` on <body>) can otherwise run after
                                                    // a caller-opened dialog's cleanup does, leaving the whole
                                                    // page unclickable until reload.
                                                    setTimeout(() => onCopyLinkClicked(), 0);
                                                }}
                                            >
                                                <Link className="h-3.5 w-3.5" />
                                                Copy link
                                            </DropdownMenuItem>
                                        )}
                                        {onDeleteClicked && (
                                            <DropdownMenuItem
                                                onSelect={() => {
                                                    setTimeout(() => onDeleteClicked(), 0);
                                                }}
                                                className="text-destructive focus:text-destructive focus:bg-destructive/10"
                                            >
                                                <Trash className="h-3.5 w-3.5" />
                                                Delete
                                            </DropdownMenuItem>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </div>
                        {/*
                         * Status, progress and due date share one line, and it survives any of
                         * them being absent: every assignment has a status, only some are part
                         * way through, and only some have a deadline.
                         *
                         * Built as a list rather than as nested conditions. Written pairwise this
                         * has gone wrong twice — once rendering the status both inside the dated
                         * line and again after it, once leaving a leading "·" on a tile that had
                         * a date and no status. A separator drawn *between* items cannot dangle.
                         */}
                        {(() => {
                            const parts = [
                                status ? (
                                    <Mono key="status" size="xs" className={STATUS_TONE_CLASS[statusTone]}>
                                        {status}
                                    </Mono>
                                ) : null,
                                progress ? (
                                    <Mono key="progress" size="xs" muted>
                                        {progress}
                                    </Mono>
                                ) : null,
                                // Lateness is the date's problem, so only this part turns red.
                                dueLabel ? (
                                    <Mono
                                        key="due"
                                        size="xs"
                                        muted={!overdue}
                                        className={cn(overdue && "text-destructive")}
                                    >
                                        {dueLabel}
                                    </Mono>
                                ) : null,
                            ].filter(Boolean);

                            if (parts.length === 0) return null;

                            return (
                                <div className="mb-1 flex items-center gap-1.5">
                                    {parts.map((part, index) => (
                                        <Fragment key={index}>
                                            {index > 0 && (
                                                <Mono size="xs" muted aria-hidden>
                                                    ·
                                                </Mono>
                                            )}
                                            {part}
                                        </Fragment>
                                    ))}
                                </div>
                            );
                        })()}
                    </div>
                    {publishedStatus && (
                        <div className="mt-0.5 flex items-center gap-1">
                            <Text size="xs" muted className="truncate">
                                {organizationName} ·{" "}
                                {publishedStatus === "PUBLISHED" ? (
                                    <span className="inline-flex items-center gap-1 align-middle">
                                        Published <BadgeCheck className="text-success h-3 w-3 shrink-0" />
                                    </span>
                                ) : (
                                    "Draft"
                                )}
                            </Text>
                        </div>
                    )}
                    {processingStatus && (
                        <div className="mt-0.5 flex items-center gap-1">
                            <Text size="xs" muted className="truncate">
                                {organizationName} ·{" "}
                                {processingStatus === "READY" ? (
                                    <span className="inline-flex items-center gap-1 align-middle">
                                        Ready <BadgeCheck className="text-success h-3 w-3 shrink-0" />
                                    </span>
                                ) : (
                                    "Processing"
                                )}
                            </Text>
                        </div>
                    )}
                    {meta && (
                        <Text size="xs" muted>
                            {meta}
                        </Text>
                    )}
                    {description && <TileDescription description={description} />}
                    {tags && tags.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-1">
                            {tags.slice(0, 2).map((tag, idx) => (
                                <Badge key={idx} variant="tag">
                                    {tag.name}
                                </Badge>
                            ))}
                            {tags.length > 2 && (
                                /*
                                 * The overflow chip names what it hides on hover and on focus —
                                 * a "+2 more" that cannot be expanded tells the operator only
                                 * that they are missing something. A real button rather than a
                                 * styled span so it is reachable by keyboard; `stopPropagation`
                                 * because the whole tile may be clickable.
                                 */
                                <TooltipProvider>
                                    <Tooltip delayDuration={0}>
                                        <TooltipTrigger asChild>
                                            <button
                                                type="button"
                                                onClick={(e) => e.stopPropagation()}
                                                className={cn(badgeVariants({ variant: "tag" }), "hover:bg-border")}
                                            >
                                                +{tags.length - 2} {moreTagsLabel}
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipPortal>
                                            <TooltipContent
                                                side="bottom"
                                                align="start"
                                                className="max-w-56 rounded-lg border-0 text-xs wrap-break-word whitespace-normal shadow-lg"
                                                style={{
                                                    backgroundColor: "var(--foreground)",
                                                    color: "var(--background)",
                                                }}
                                            >
                                                {tags
                                                    .slice(2)
                                                    .map((tag) => tag.name)
                                                    .join(", ")}
                                            </TooltipContent>
                                        </TooltipPortal>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export { ContentTile };
